import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import OfferPopup from '../components/OfferPopup';
import { fetchCollection, getVal } from '../lib/api';

const resizeAsset = (path, width, quality = 85) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto${path}`;
};

const heroAssetPath = '/assets/27b85900-9717-41d2-8622-634ea09330b6/imgi-2-595527187-122169759662785719-5797550808910282226-n.jpg';
const heroImage = resizeAsset(heroAssetPath, 1200);
const heroImageSrcSet = [900, 1200, 1600]
  .map((width) => `${resizeAsset(heroAssetPath, width)} ${width}w`)
  .join(', ');

const heroContacts = [
  {
    label: 'Booking',
    value: '22 42 60 11',
    href: 'tel:22426011',
  },
  {
    label: 'E-mail',
    value: 'J_danielsen85@hotmail.com',
    href: 'mailto:J_danielsen85@hotmail.com',
  },
];

const quickTags = ['Udlejning til fester', 'Levering & opsætning', 'Telt, lyd og maskiner', 'Klar til dagen'];

const featureCards = [
  {
    title: 'Teltudlejning',
    description: 'Partytelte fra 6x3m op til 6x27m og 9x12m – leveret, opstillet og nedtaget til din fest.',
    imagePath: '/assets/27b85900-9717-41d2-8622-634ea09330b6/imgi-2-595527187-122169759662785719-5797550808910282226-n.jpg',
    to: '/telt-udlejning',
    accent: 'featured',
  },
  {
    title: 'Hoppeborg i haven',
    description: 'Den ultimative underholdning til børnefødselsdagen eller sommerfesten. Inkl. levering & opsætning.',
    imagePath: '/assets/27b85900-9717-41d2-8622-634ea09330b6/image18_3.jpeg',
    to: '/udlejning/hoppeborg',
    accent: 'featured',
  },
  {
    title: 'Slushice maskine',
    description: 'En enkel og festlig løsning, når gæsterne skal have noget koldt og farverigt i glasset.',
    imagePath: '/assets/27b85900-9717-41d2-8622-634ea09330b6/imgi-73-498030673-122133800990785719-4494842369837955426-n.jpg',
    to: '/udlejning/slushice-maskine',
    accent: 'white',
  },
  {
    title: 'Soundbox 3',
    description: 'Lyd til taler, musik og fantastisk stemning, pakket ind i en mobil og kraftfuld løsning.',
    imagePath: '/assets/27b85900-9717-41d2-8622-634ea09330b6/84984-3_2.jpg',
    to: '/udlejning/soundbox-3',
    accent: 'white',
  },
];

function FeatureCard({ title, description, imagePath, to, accent }) {
  const featured = accent === 'featured';

  return (
    <Link to={to || "/udlejning"} aria-label={`Gå til: ${title}`} className="group block h-full">
      <article
        className={`relative h-full overflow-hidden rounded-[2rem] border ${featured ? 'border-brand-sky bg-brand-sky text-white shadow-[0_28px_70px_rgba(29,78,216,0.22)]' : 'border-brand-orange/20 bg-white text-slate-900 shadow-[0_20px_50px_rgba(15,23,42,0.08)]'} px-6 py-8 transition-transform duration-300 group-hover:-translate-y-1 sm:px-8 sm:py-10 flex flex-col justify-between`}
      >
        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.4em] ${featured ? 'text-white/70' : 'text-brand-sky'}`}>
                Udlejning
              </p>
              <h3 className="mt-3 font-display text-2xl leading-tight tracking-[-0.03em] sm:text-3xl">{title}</h3>
            </div>
            <i className={`fa-solid fa-arrow-right mt-1 text-sm transition-transform group-hover:translate-x-1 ${featured ? 'text-white' : 'text-brand-sky'}`} aria-hidden="true" />
          </div>

          <div className="mt-8 flex justify-center">
            <div className={`relative h-44 w-44 overflow-hidden rounded-full border-4 ${featured ? 'border-white/30' : 'border-slate-100'} bg-slate-100 sm:h-52 sm:w-52`}>
              <img
                src={resizeAsset(imagePath, 560, 80)}
                alt={title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
          </div>

          <p className={`mt-8 text-sm leading-7 ${featured ? 'text-white/80' : 'text-slate-600'}`}>{description}</p>
        </div>

        <div className={`mt-8 h-px w-16 ${featured ? 'bg-white/35' : 'bg-brand-orange/25'}`} />
      </article>
    </Link>
  );
}

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [featuredItems, setFeaturedItems] = useState([]);

  useEffect(() => {
    async function loadCmsData() {
      try {
        const [pageRes, contactRes, featuredRes] = await Promise.all([
          fetchCollection('sider', { term: 'sidetype:forside' }),
          fetchCollection('kontakt_information'),
          fetchCollection('priser_og_pakker', { term: 'fremhaevet:fremhaevet' }),
        ]);

        if (pageRes.items && pageRes.items.length > 0) {
          setPageData(pageRes.items[0]);
        }
        if (contactRes.items && contactRes.items.length > 0) {
          setContactInfo(contactRes.items[0]);
        }
        if (featuredRes.items && featuredRes.items.length > 0) {
          setFeaturedItems(featuredRes.items);
        }
      } catch (err) {
        console.error('Fejl ved hentning af CMS-data til Forside:', err);
      }
    }
    loadCmsData();
  }, []);

  const heroContacts = [
    {
      label: 'Booking',
      value: contactInfo?.phone || '22 42 60 11',
      href: `tel:${(contactInfo?.phone || '22426011').replace(/\s+/g, '')}`,
    },
    {
      label: 'E-mail',
      value: contactInfo?.email || 'J_danielsen85@hotmail.com',
      href: `mailto:${contactInfo?.email || 'J_danielsen85@hotmail.com'}`,
    },
  ];

  return (
    <div className="bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.18),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.12),_transparent_40%),linear-gradient(180deg,#f9fdff_0%,#ffffff_58%)]" />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 sm:pb-20 lg:px-8 lg:pb-24 lg:pt-10">
          <div className="grid items-center gap-10 lg:grid-cols-[0.98fr_1.02fr] lg:gap-16 xl:gap-20">
            <div className="relative z-10 max-w-2xl">
              <ScrollReveal animation="fade-in-left">
                <p className="text-[11px] font-semibold uppercase tracking-[0.45em] text-brand-sky">
                  {getVal(pageData?.subtitle) || 'Udlejning af maskiner og festudstyr'}
                </p>
                <h1 className="mt-4 max-w-lg font-display text-[clamp(2.9rem,6vw,5rem)] leading-[0.9] tracking-[-0.05em] text-slate-900 sm:text-[clamp(3.2rem,5vw,5.4rem)]">
                  {getVal(pageData?.title) || 'Udlejning til fester'}
                </h1>
                {pageData?.content ? (
                  <div
                    className="mt-6 max-w-lg text-base leading-7 text-slate-600 sm:text-lg"
                    dangerouslySetInnerHTML={{ __html: getVal(pageData.content) }}
                  />
                ) : (
                  <p className="mt-6 max-w-lg text-base leading-7 text-slate-600 sm:text-lg">
                    En lys, enkel og elegant løsning til fester, hvor slushice, lyd, telte og bordløsninger samles i ét overskueligt setup.
                  </p>
                )}
              </ScrollReveal>

              <ScrollReveal animation="fade-in-left" delay={150}>
                <div className="mt-10 flex max-w-sm flex-col gap-4">
                  {heroContacts.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="group rounded-[1.5rem] border border-brand-orange/15 bg-white/90 px-5 py-5 shadow-[0_14px_35px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-brand-sky">{item.label}</p>
                      <p className="mt-2 text-base leading-7 text-slate-900 transition-colors group-hover:text-brand-sky sm:text-lg">
                        {item.value}
                      </p>
                    </a>
                  ))}
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal animation="scale-up" delay={100} className="relative">
              <div className="relative overflow-hidden rounded-[2.25rem] border border-brand-orange/15 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
                <img
                  src={heroImage}
                  srcSet={heroImageSrcSet}
                  sizes="(min-width: 1024px) 52vw, 100vw"
                  alt="Festudstyr og stemningsfuld opsætning"
                  className="h-[34rem] w-full object-cover object-[center_32%] sm:h-[40rem] lg:h-[48rem] transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-950/30 via-transparent to-transparent" />
                <div className="absolute left-6 top-6 h-3 w-3 rounded-full bg-brand-sky animate-pulse-slow shadow-[0_0_0_12px_rgba(29,78,216,0.12)]" />
                <div className="absolute right-8 bottom-8 h-4 w-4 rounded-full bg-brand-orange animate-float shadow-[0_0_0_12px_rgba(235,163,21,0.15)]" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section id="services" className="relative z-10 mx-auto mt-16 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <ScrollReveal animation="fade-in-down">
            <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Vores vigtigste festudstyr</span>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-[-0.03em] text-slate-900 sm:text-4xl">
              Udlejning af telte, hoppeborg & maskiner
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-xl mx-auto">
              Vi satser især på professionel teltudlejning og festlige hoppeborge – med komplet levering, opsætning og nedtagning.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 items-stretch">
          {featureCards.map((card, idx) => (
            <ScrollReveal key={card.title} animation="fade-in-up" delay={idx * 100}>
              <FeatureCard {...card} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Special Showcase Section for Telt & Hoppeborg with multiple tent pictures and garden bouncy castle */}
      <section className="bg-stone-50 py-20 border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Fokusområder</span>
              <h2 className="mt-2 font-display text-3xl font-bold text-slate-800 sm:text-4xl">
                Se vores telte og hoppeborge
              </h2>
              <p className="mt-2 text-slate-500 text-sm max-w-lg">
                Klik på billederne for at læse mere om vores teltstørrelser eller leje af hoppeborgen til haven.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/telt-udlejning"
                className="rounded-full bg-brand-orange px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-all hover:bg-brand-orange-hover"
              >
                Se alle telte
              </Link>
              <Link
                to="/udlejning/hoppeborg"
                className="rounded-full border border-stone-300 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700 transition-all hover:border-brand-orange hover:text-brand-orange"
              >
                Hoppeborg leje
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Telt billede 1 */}
            <ScrollReveal animation="fade-in-up" delay={0}>
              <Link to="/telt-udlejning" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/imgi-2-595527187-122169759662785719-5797550808910282226-n.jpg"
                    alt="Festtelt på græs"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-sky px-3 py-1 text-xs font-bold text-white shadow">
                    Teltudlejning
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Festtelt i haven</h3>
                  <p className="mt-1 text-sm text-slate-500">Klassiske partytelte fra 6x3m op til 6x27m med opsætning.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    Læs mere & priser <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Hoppeborg i haven billede (image18_3) */}
            <ScrollReveal animation="fade-in-up" delay={100}>
              <Link to="/udlejning/hoppeborg" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/image18_3.jpeg"
                    alt="Hoppeborg i haven"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-orange px-3 py-1 text-xs font-bold text-white shadow">
                    Hoppeborg
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Hoppeborg i haven</h3>
                  <p className="mt-1 text-sm text-slate-500">Sikkerhedsgodkendt hoppeborg – inklusiv levering, opsætning og blæser.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    1.500 kr. / døgn <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Telt billede 2 (stort telt / image32) */}
            <ScrollReveal animation="fade-in-up" delay={200}>
              <Link to="/telt-udlejning" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/image32.jpeg"
                    alt="Stort telt 9x12m"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-sky px-3 py-1 text-xs font-bold text-white shadow">
                    Stort Telt 9x12m
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Rummelige rammer til store fester</h3>
                  <p className="mt-1 text-sm text-slate-500">Stort 9x12m festtelt – kan opdeles i 3m sektioner samt lægges trægulv.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    Se teltudvalg <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Telt billede 3 (opdækning/interiør) */}
            <ScrollReveal animation="fade-in-up" delay={300}>
              <Link to="/telt-udlejning" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/forside_4.jpg"
                    alt="Telt med opdækning, borde og stole"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-slate-800/80 px-3 py-1 text-xs font-bold text-white shadow backdrop-blur">
                    Borde & Stole
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Komplet teltpakke</h3>
                  <p className="mt-1 text-sm text-slate-500">Lej runde og firkantede borde, stole og lyskæder direkte med teltet.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    Se tilbehør <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Hoppeborg i haven vinkel 2 (image19_3) */}
            <ScrollReveal animation="fade-in-up" delay={400}>
              <Link to="/udlejning/hoppeborg" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/image19_3.jpeg"
                    alt="Hoppeborg klar til fest i haven"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-orange px-3 py-1 text-xs font-bold text-white shadow">
                    Havefest & Børn
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Timer af sjov for børnene</h3>
                  <p className="mt-1 text-sm text-slate-500">Passer perfekt i de fleste private haver og græsarealer.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    Book hoppeborg <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Telt billede 4 (telt1_2.jpg) */}
            <ScrollReveal animation="fade-in-up" delay={500}>
              <Link to="/telt-udlejning" className="group block overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/telt1_2.jpg"
                    alt="Telt opstilling"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-sky px-3 py-1 text-xs font-bold text-white shadow">
                    Levering & Opsætning
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 group-hover:text-brand-orange transition-colors">Bekymringsfri opsætning</h3>
                  <p className="mt-1 text-sm text-slate-500">Vi opsætter og nedtager teltet så alt står helt klar til dine gæster.</p>
                  <span className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wider text-brand-orange">
                    Kontakt for tilbud <i className="fa-solid fa-arrow-right ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-28 text-white sm:py-32 lg:py-36">
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/imgi-2-595527187-122169759662785719-5797550808910282226-n.jpg"
            alt=""
            className="h-full w-full object-cover transition-transform duration-[10s] hover:scale-105"
          />
          <div className="absolute inset-0 bg-brand-sky/82" />
        </div>
        <div className="absolute inset-x-0 top-0 -translate-y-1">
          <svg viewBox="0 0 1440 120" className="block h-16 w-full text-white" preserveAspectRatio="none" aria-hidden="true">
            <path
              fill="currentColor"
              d="M0,56 C120,18 240,18 360,44 C480,70 600,98 720,90 C840,82 960,36 1080,30 C1200,24 1320,62 1440,50 L1440,0 L0,0 Z"
            />
          </svg>
        </div>
        <div className="absolute inset-x-0 bottom-0 translate-y-1">
          <svg viewBox="0 0 1440 120" className="block h-16 w-full text-white" preserveAspectRatio="none" aria-hidden="true">
            <path
              fill="currentColor"
              d="M0,62 C120,96 240,98 360,76 C480,54 600,22 720,30 C840,38 960,84 1080,88 C1200,92 1320,54 1440,62 L1440,120 L0,120 Z"
            />
          </svg>
        </div>
        <div className="relative z-10 mx-auto flex min-h-[22rem] max-w-7xl flex-col items-center justify-center px-4 text-center sm:min-h-[26rem] lg:min-h-[30rem] sm:px-6 lg:px-8">
          <ScrollReveal animation="scale-up" className="flex w-full flex-col items-center">
            <h2 className="mt-6 font-display text-3xl font-bold leading-tight tracking-[-0.03em] text-white sm:text-4xl md:text-5xl lg:text-6xl">
              Skab uforglemmelige rammer om din næste begivenhed
            </h2>

            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
              Vi leverer alt fra festtelte, borde og stole til professionelle maskiner og festudstyr.
              Hurtig levering, høj kvalitet og personlig service, der sikrer en bekymringsfri dag.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/udlejning"
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-brand-sky shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-xl"
              >
                Se udvalg
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
              >
                Få et tilbud
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-center">
            <ScrollReveal animation="fade-in-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.45em] text-brand-sky">Klar til næste fest</p>
              <h2 className="mt-4 max-w-xl font-display text-4xl leading-none tracking-[-0.03em] text-slate-900 sm:text-5xl lg:text-6xl">
                En rolig og lækker vej til en god fest
              </h2>
            </ScrollReveal>
            <ScrollReveal animation="fade-in-right" className="flex flex-col items-start gap-3 lg:items-end lg:text-right">
              <Link
                to="/kontakt"
                className="inline-flex w-fit items-center justify-center rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white shadow-lg shadow-brand-orange/25 transition-all duration-300 hover:-translate-y-1 hover:bg-brand-orange-hover hover:shadow-xl"
              >
                Kontakt os
              </Link>
            </ScrollReveal>
          </div>
          <div className="mt-16 h-px w-full bg-slate-200" />
        </div>
      </section>

      <OfferPopup />
    </div>
  );
}
